<template>
  <base-layout>
    <h1 slot="header">
      {{ `app.pages.subscription` | trans }}
    </h1>
    <a slot="buttons" :href="routes.orders_and_invoices" class="btn btn-primary">
      <i class="fa fa-arrow-circle-left" aria-hidden="true"></i>
      Wróć
    </a>
    <v-alert :value="showPayAlert" dismissible @input="payAlertDismiss" type="success">
      <h4>Szanowny Użytkowniku!</h4>
      <p>
        Aby dokończyć składanie zamówienia nr <strong>{{ order.id }}</strong> przedłużającego okres korzystania z
        programu Dietico prosimy o jego opłacenie poprzez wybranie przycisku poniżej: "Płacę z...".
      </p>
    </v-alert>
    <v-alert :value="showOrderClosedAlert" dismissible @input="orderClosedAlertDismiss" type="success">
      <h4>Szanowny Użytkowniku!</h4>
      <p>
        Zamówienie nr <strong>{{ order.id }}</strong> zostało już opłacone.
      </p>
    </v-alert>
    <v-alert :value="showStudentStatusVerificationInProgressAlert" type="warning">
      <h4>Szanowny Użytkowniku!</h4>
      <p>
        Trwa weryfikacja Twojego statusu studenta. O wyniku weryfikacji zostaniesz poinformowany w kolejnym emailu w
        ciągu jednego dnia. Po pomyślnej weryfikacji możliwe będzie opłacenie abonamentu.
      </p>
    </v-alert>
    <v-app id="vuetify-app" class="no--wrap box box-success">
      <v-container>
        <v-layout justify-center row wrap class="box-body">
          <v-flex xs12>
            <h3 class="subscription-order__header">
              Wybrany pakiet: <strong>{{ order.subscription_type.name }}</strong>
            </h3>
            <h4 class="subscription-order__header header--dates">
              {{ getSubscriptionStartDate().format('DD.MM.Y') }} - {{ getSubscriptionEndDate().format('DD.MM.Y') }}
            </h4>
            <hr/>
          </v-flex>
          <v-flex v-if="showVATTemplate" xs6 class="subscription-order__address">
            <span class="address__header">SPRZEDAWCA</span>
            {{ seller.name }}
            {{ seller.address }}
            {{ seller.address2 }}
            {{ seller.nip }}
          </v-flex>
          <v-flex v-else xs6 class="subscription-order__address">
            <span class="address__header">SPRZEDAWCA</span>
            {{ seller.name }}
            {{ seller.address }}
            {{ seller.address2 }}
            {{ seller.nip }}
          </v-flex>
          <v-flex xs6 class="subscription-order__address">
            <span class="address__header">NABYWCA</span>
            {{ order.customer_name }}
            {{ order.customer_street_address }}
            {{ order.customer_postal_code }} {{ order.customer_city }}
            {{ countryName(order.customer_country) }}
            <span v-if="order.customer_type === 'company'">NIP: {{ order.customer_NIP }}</span>
          </v-flex>
          <v-flex v-if="showVATTemplate" xs12>
            <table class="table">
              <thead>
              <tr>
                <th>NAZWA USŁUGI</th>
                <th width="150" class="text-right">NETTO</th>
                <th width="150" class="text-right">STAWKA VAT</th>
                <th width="150" class="text-right">VAT</th>
                <th width="150" class="text-right">BRUTTO</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>{{ order.subscription_type.name }} - {{ getSubscriptionDurationLabel() }}</td>
                <td width="150" class="text-right">{{ getNetPrice() | money }}</td>
                <td width="150" class="text-right">23%</td>
                <td width="150" class="text-right">{{ getVAT() | money }}</td>
                <td width="150" class="text-right">{{ getGrossPrice() | money }}</td>
              </tr>
              <tr>
                <td colspan="4" class="text-bold text-right">RAZEM DO ZAPŁATY:</td>
                <td class="text-bold text-right">{{ getGrossPrice() | money }}</td>
              </tr>
              </tbody>
            </table>
          </v-flex>
          <v-flex v-else xs12>
            <table class="table">
              <thead>
              <tr>
                <th>NAZWA USŁUGI</th>
                <th width="150" class="text-right">BRUTTO</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>{{ order.subscription_type.name }} - {{ getSubscriptionDurationLabel() }}</td>
                <td width="150" class="text-right">{{ getGrossPrice() | money }}</td>
              </tr>
              <template v-if="this.order.status === 'closed'">
                <template v-for="discount in order_discounts">
                  <tr>
                    <td>{{ discount.label }}</td>
                    <td width="150" class="text-right">- {{ discount.value | money }}</td>
                  </tr>
                </template>
                <tr>
                  <td class="text-bold text-right">RAZEM DO ZAPŁATY:</td>
                  <td class="text-bold text-right">{{ finalPriceUserSummary | money }}</td>
                </tr>
              </template>
              <template v-else>
                <tr
                    v-if="
                      isUpgradingSubscription() &&
                      current_subscription.unused_days > 0 &&
                      current_subscription.subscription_type.role_to_assign !== 'dietitian_test'
                    "
                >
                  <td>
                    {{
                      trans('app.discount_codes.unused_days_discount', {
                        count: current_subscription.unused_days,
                        days: polishPlurals('dzień', 'dni', 'dni', current_subscription.unused_days),
                        subscription: current_subscription.subscription_type.name
                      })
                    }}
                  </td>
                  <td width="150" class="text-right">- {{ unusedDaysValue | money }}</td>
                </tr>
                <tr v-if="discount_code !== null">
                  <td>{{ trans('app.discount_codes.discount_code_discount', { discount_code: discount_code.code }) }}</td>
                  <td width="150" class="text-right">-{{ discount_code.value | money }}</td>
                </tr>
                <tr>
                  <td class="text-bold text-right">RAZEM DO ZAPŁATY:</td>
                  <td class="text-bold text-right">{{ finalPrice | money }}</td>
                </tr>
              </template>
              </tbody>
            </table>
          </v-flex>
        </v-layout>
        <v-layout row wrap align-center justify-center v-if="order.status === 'open'">
          <v-flex xs12 class="text-center" v-if="payments.p24">
            <h4>Płatności obsługiwane przez:</h4>
          </v-flex>
          <v-flex xs12 v-if="payments.p24">
            <v-layout align-center justify-center column fill-height>
              <img :src="'/img/przelewy24_logo.png'" alt="Płatności obsługiwane przez Przelewy24"/>
              <v-btn :disabled="getPayWithButtonDisabled()" color="primary" @click="payWithP24()">
                Płacę z Przelewy24
              </v-btn>
            </v-layout>
          </v-flex>
          <v-flex xs12 class="text-center" v-if="payments.payu">
            <h4 class="pt-5">Dla klientów zagranicznych / płatności kartą:</h4>
          </v-flex>
          <v-flex xs12 v-if="payments.payu">
            <v-layout align-center justify-center column fill-height>
              <img :src="'/img/payu-logo.png'" alt="Płatności kartą obsługiwane przez PayU"/>
              <v-btn :disabled="getPayWithButtonDisabled()" color="primary" @click="payWithPayU()">
                Płacę z PayU
              </v-btn>
            </v-layout>
          </v-flex>
        </v-layout>
        <v-layout align-center justify-center row wrap v-else-if="order.status === 'closed'">
          <v-btn :disabled="formSubmission" color="primary" :href="routes.orders_and_invoices">
            Wróć do listy zamówień
          </v-btn>
        </v-layout>
      </v-container>
    </v-app>
  </base-layout>
</template>

<style lang="scss" type="text/scss">
.subscription-order__header {
  text-align: center;
}

.subscription-order__header.header--dates {
  margin-bottom: 35px;
}

.subscription-order__address {
  white-space: pre-line;
}

.address__header {
  display: block;
  font-weight: bold;
}

.subscription-order__footer {
  border-top: 1px solid #f4f4f4;
}
</style>

<script>
import {mapGetters} from 'vuex';
import moment from 'moment';
import {vat, net} from '../../utils/money';
import {newSubscriptionEndDate, newSubscriptionStartDate} from '../../utils/subscription';
import {polishPlurals} from 'polish-plurals';

export default {
  props: {
    routes: Object,
    order: Object,
    seller: Object,
    last_subscription: Object,
    current_subscription: Object,
    payments: Object,
    discount_code: {
      required: false,
      type: Object,
    },
    order_discounts: {
      required: false,
      type: Array,
    }
  },
  data: () => ({
    formSubmission: false,
    showPayAlert: false,
    showOrderClosedAlert: false,
    showVATTemplate: false,
    showStudentStatusVerificationInProgressAlert: false,
    unusedDaysValue: 0,
  }),
  created() {
    if (
        this.order.status === 'open' &&
        (this.order.subscription_type.role_to_assign !== 'dietitian_student' || this.order.student_status_verified)
    ) {
      this.showPayAlert = true;
    }

    if (this.order.status === 'closed') {
      this.showOrderClosedAlert = true;
    }

    if (
        this.order.status === 'open' &&
        this.order.subscription_type.role_to_assign === 'dietitian_student' &&
        !this.order.student_status_verified
    ) {
      this.showStudentStatusVerificationInProgressAlert = true;
    }

    this.showVATTemplate = this.resolveShowVATTemplate();

    if (this.isUpgradingSubscription()) {
      this.unusedDaysValue = this.current_subscription.unused_days_value;
    }
  },
  methods: {
    payWithPayU() {
      this.initializePayment('payu');
    },
    payWithP24() {
      this.initializePayment('p24');
    },
    initializePayment(paymentType) {
      this.formSubmission = true;
      axios
          .post(`/payments/initialize/${paymentType}/${this.order.uuid}`)
          .then((response) => {
            window.location.href = response.data.redirect_route;
          })
          .catch((error) => {
            this.$toast.error('Nie udało się złożyć zamówienia');
            this.sentryCaptureException(error);
          })
          .finally(() => {
            this.formSubmission = false;
          });
    },
    payAlertDismiss() {
      this.showPayAlert = false;
    },
    orderClosedAlertDismiss() {
      this.showOrderClosedAlert = false;
    },
    resolveShowVATTemplate() {
      return moment(this.order.created_at).unix() < moment('2019-02-01').unix();
    },
    getNetPrice() {
      return net(this.getGrossPrice());
    },
    getVAT() {
      return vat(this.getGrossPrice());
    },
    getGrossPrice() {
      return Number(this.order.amount);
    },
    getSubscriptionStartDate() {
      if (this.order.status === 'closed') {
        return moment(this.order.start_date);
      }
      return newSubscriptionStartDate(
          this.isUpgradingSubscription(),
          this.current_subscription,
          this.last_subscription
      );
    },
    getSubscriptionEndDate() {
      if (this.order.status === 'closed') {
        return moment(this.order.end_date);
      }

      return newSubscriptionEndDate(
          this.getSubscriptionDuration(),
          this.isUpgradingSubscription(),
          this.current_subscription,
          this.last_subscription
      );
    },
    getPayWithButtonDisabled() {
      if (this.formSubmission) {
        return true;
      }

      return this.order.subscription_type.role_to_assign === 'dietitian_student' && !this.order.student_status_verified;
    },
    polishPlurals(singularNominativ, pluralNominativ, pluralGenitive, value) {
      return polishPlurals(singularNominativ, pluralNominativ, pluralGenitive, value);
    },
    isUpgradingSubscription() {
      if (this.current_subscription === null) {
        return false;
      }

      if (this.current_subscription.subscription_type.role_to_assign === 'dietitian_test') {
        return false;
      }

      if (this.order.subscription_type.id === 30) {
        return false;
      }

      let currentRole = null;

      if (this.current_subscription.subscription_type.id === 30) {
        currentRole = this.current_subscription.order.role_to_assign;
      } else {
        currentRole = this.current_subscription.subscription_type.role_to_assign;
      }

      const subscriptionsRoles = ['dietitian_student', 'dietitian_standard', 'dietitian_premium'];
      const newRole = this.order.subscription_type.role_to_assign;
      const currentRoleIndex = subscriptionsRoles.indexOf(currentRole);
      const newRoleIndex = subscriptionsRoles.indexOf(newRole);
      return newRoleIndex > currentRoleIndex;
    },
    getSubscriptionDuration() {
      if (this.order.subscription_type.id === 30) {
        return this.order.duration;
      }
      if (this.discount_code) {
        return this.order.subscription_type.duration;
      }
      return this.order.subscription_type.duration + this.order.subscription_type.extra_duration;
    },
    getSubscriptionDurationLabel() {
      const duration = this.getSubscriptionDuration();
      return duration + ' ' + polishPlurals('dzień', 'dni', 'dni', duration);
    },
  },
  computed: {
    ...mapGetters(['countryName']),
    finalPrice() {
      const grossPrice = this.getGrossPrice();
      const unusedDaysValue = this.unusedDaysValue;
      const discount = this.discount_code !== null ? this.discount_code.value : 0;
      const finalPrice = grossPrice - unusedDaysValue - discount;

      return finalPrice > 0 ? finalPrice : 0.01;
    },
    finalPriceUserSummary() {
      const grossPrice = this.getGrossPrice();
      const fullDiscount = this.order.discount;
      const finalPrice = grossPrice - fullDiscount;

      return finalPrice <= 0 ? 0.01 : finalPrice;
    },
  },
};
</script>
